import React, { useState } from 'react';
import { Dropdown, Label, SemanticCOLORS } from 'semantic-ui-react';
import { Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow } from '../../../../common/components/Table';
import { PageSection, Subtitle } from 'common/styles/page';
import { StyledAddButton } from 'common/components/button/StyledButton';
import OrganizationRecordModal from './OrganizationRecordModal';
import { organizationRecordValueApi } from '../api/organizationRecordValueApi';
import { OrganizationRecordValue } from 'store/generated/admin/organization-record-values';
import DeleteConfirmationModal from './OrganizationEmployeeRecordDeleteModal';
import LoadingSpinner from 'common/components/LoadingSpinner';

interface OrganizationRecordsListProps {
  organizationId: number;
}

const OrganizationRecordsList: React.FC<OrganizationRecordsListProps> = ({ organizationId }) => {

  const { data, isLoading, error } = organizationRecordValueApi.endpoints.apiAdminV1OrganizationRecordValuesList.useQuery(
    {
      organizationId: organizationId
    }
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<OrganizationRecordValue | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<OrganizationRecordValue | null>(null);

  const [deleteFieldType] = organizationRecordValueApi.endpoints.apiAdminV1OrganizationRecordValuesDestroy.useMutation();

  const confirmDelete = async () => {
    if (!fieldToDelete) return;
    try {
      await deleteFieldType({ id: fieldToDelete.id }).unwrap();
      setShowDeleteModal(false);
      setFieldToDelete(null);
    } catch (err) {
      console.error('Failed to delete field type:', err);
    }
  };

  const getTypeOfValueLabel = (type: number): { name: string; color: SemanticCOLORS } => {
    switch (type) {
      case 1:
        return { name: 'Text/Character', color: 'green' };
      case 2:
        return { name: 'Number', color: 'blue' };
      case 3:
        return { name: 'Decimal', color: 'orange' };
      case 4:
        return { name: 'Date', color: 'purple' };
      default:
        return { name: 'Unknown', color: 'grey' };
    }
  };

  if (isLoading) {
    return (<LoadingSpinner />);
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <h2 style={{ color: 'red' }}>Error loading organization records</h2>
      </div>
    );
  }

  return (
    <PageSection>
      <PageSection display="flex" justifyContent="space-between" alignItems="center" padtop="35px">
        <Subtitle>Organization Records</Subtitle>
        <StyledAddButton
          onClick={() => {
            setSelectedField(null);
            setIsModalOpen(true);
          }}
          label="Create Organization Record"
          width="240px"
        />


      </PageSection>
      <Table>
        <TableHeader>
          <TableRow>
            <HeaderCell width={7}>Organization Record</HeaderCell>
            <HeaderCell width={7}>Type</HeaderCell>
            <HeaderCell width={2} justify="center">Actions</HeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.map(fieldType => (
            <TableRow key={fieldType.id} $selectable onClick={() => {
              setSelectedField(fieldType);
              setIsModalOpen(true);
            }}>
              <TableCell $firstcol>
                {fieldType.name}
              </TableCell>
              <TableCell>
                {(() => {
                  const typeLabel = getTypeOfValueLabel(fieldType.type_of_value);
                  return <Label color={typeLabel.color}>{typeLabel.name}</Label>;
                })()}
              </TableCell>
              <TableCell width={2} justify="center">
                <Dropdown
                  button
                  icon="ellipsis vertical"
                  floating
                  className="icon"
                  direction="left"
                  style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                  onClick={e => e.stopPropagation()}
                >
                  <Dropdown.Menu>
                    <>
                      <Dropdown.Item
                        text="Edit Organization Record"
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedField(fieldType);
                          setIsModalOpen(true);
                        }}
                      />

                      <Dropdown.Item
                        text="Delete Organization Record"
                        onClick={e => {
                          e.stopPropagation();
                          setFieldToDelete(fieldType);
                          setShowDeleteModal(true);
                        }}
                      />

                    </>
                  </Dropdown.Menu>
                </Dropdown>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>


      <OrganizationRecordModal
        open={isModalOpen}
        organizationId={organizationId}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          setIsModalOpen(false);
        }}
        fieldToEdit={selectedField}
      />

      <DeleteConfirmationModal
        open={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setFieldToDelete(null);
        }}
        onConfirm={confirmDelete}
        itemName={fieldToDelete?.name}
      />

    </PageSection>
  );
};

export default OrganizationRecordsList;
